import {useQueryClient} from '@tanstack/react-query';
import {FormInstance} from 'antd';

import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {LangKeyType} from '../../../../../../../../provider/locale/translation/type';
import {useSnackbar} from '../../../../../../../../provider/snackbar/snackbar-hook';
import {CatalogByGroupType} from '../../../../../../../../service/source-settings/catalogs/catalogs-type';
import {
    invalidateStoreAccountsQueries,
    useBindStoreAccountAppsMutation,
    useCreateStoreAccountMutation,
    useValidateStoreAccountMutation,
} from '../../../../../../../../service/source-settings/store-accounts/store-accounts-hook';

import {buildStoreAccountBindLinks, getStoreAccountErrorMessage, readFileAsBase64} from './add-appstore-account-helper';
import {AppstoreApiKeyFormEnum, AppstoreApiKeyFormType} from './appstore-api-key-form/appstore-api-key-form-type';
import {AppstoreAppsFormType} from './appstore-apps-form/appstore-apps-form-type';
import {AppstoreInvalidCredentialsNotification} from './appstore-invalid-credentials-notification/appstore-invalid-credentials-notification';

type ValidateAndCreateReturnType = {
    isLoading: boolean;
    validateAndCreate: (
        catalog: CatalogByGroupType,
        values: AppstoreApiKeyFormType,
        apiKeyForm: FormInstance<AppstoreApiKeyFormType>
    ) => Promise<number | null>;
};

export function useValidateAndCreateAppstoreAccount(): ValidateAndCreateReturnType {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const {isLoading: isValidateLoading, mutateAsync: validateAsync} = useValidateStoreAccountMutation();
    const {isLoading: isCreateLoading, mutateAsync: createAsync} = useCreateStoreAccountMutation();

    async function validateAndCreate(
        catalog: CatalogByGroupType,
        values: AppstoreApiKeyFormType,
        apiKeyForm: FormInstance<AppstoreApiKeyFormType>
    ): Promise<number | null> {
        const failTitle = getLocalizedString('SOURCE_SETTINGS__ACCOUNTS__ADD_FAIL_TITLE');
        const failText = getLocalizedString('SOURCE_SETTINGS__ACCOUNTS__ADD_FAIL_TEXT');

        apiKeyForm.setFields([
            {name: AppstoreApiKeyFormEnum.issuerId, errors: []},
            {name: AppstoreApiKeyFormEnum.keyId, errors: []},
            {name: AppstoreApiKeyFormEnum.privateKey, errors: []},
        ]);

        try {
            const base64 = await readFileAsBase64(values.privateKey);
            const credentials = {
                issuerId: values.issuerId.trim(),
                keyId: values.keyId.trim(),
                privateKey: base64,
            };

            try {
                await validateAsync({catalogId: catalog.id, credentials});
            } catch {
                snackbar.error({
                    message: getLocalizedString('SOURCE_SETTINGS__ACCOUNTS__APPSTORE__INVALID_CREDENTIALS__TITLE'),
                    description: <AppstoreInvalidCredentialsNotification />,
                });

                return null;
            }

            const {id: newAccountId} = await createAsync({catalogId: catalog.id, credentials});

            return newAccountId;
        } catch (error: unknown) {
            snackbar.error({
                message: failTitle,
                description: getStoreAccountErrorMessage(error, failText),
            });

            return null;
        }
    }

    return {
        isLoading: isValidateLoading || isCreateLoading,
        validateAndCreate,
    };
}

type BindAndFinishArgsType = {
    accountId: number;
    failTitleKey?: LangKeyType;
    onComplete: () => void;
    onSuccess: () => void;
    successTitleKey?: LangKeyType;
    values: AppstoreAppsFormType;
};

type BindAndFinishReturnType = {
    bindAndFinish: (args: BindAndFinishArgsType) => Promise<void>;
    isLoading: boolean;
};

export function useBindAppstoreAccountAppsAndFinish(): BindAndFinishReturnType {
    const queryClient = useQueryClient();
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const {isLoading, mutateAsync: bindAsync} = useBindStoreAccountAppsMutation();

    async function bindAndFinish(args: BindAndFinishArgsType): Promise<void> {
        const {
            accountId,
            failTitleKey = 'SOURCE_SETTINGS__ACCOUNTS__ADD_FAIL_TITLE',
            onComplete,
            onSuccess,
            successTitleKey = 'SOURCE_SETTINGS__ACCOUNTS__ADD_SUCCESS_TITLE',
            values,
        } = args;
        const failTitle = getLocalizedString(failTitleKey);
        const failText = getLocalizedString('SOURCE_SETTINGS__ACCOUNTS__ADD_FAIL_TEXT');
        const successTitle = getLocalizedString(successTitleKey);

        try {
            await bindAsync({
                accountId,
                payload: {
                    links: buildStoreAccountBindLinks(values),
                },
            });

            await invalidateStoreAccountsQueries(queryClient);

            snackbar.success({
                message: successTitle,
                description: getLocalizedString('SOURCE_SETTINGS__ACCOUNTS__ADD_SUCCESS_TEXT'),
            });
            onSuccess();
            onComplete();
        } catch (error: unknown) {
            snackbar.error({
                message: failTitle,
                description: getStoreAccountErrorMessage(error, failText),
            });
        }
    }

    return {
        bindAndFinish,
        isLoading,
    };
}
